import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import api from '../services/apiService';

const LwDamageLogList = () => {
  const navigate = useNavigate();

  const [serverFilterOptions, setServerFilterOptions] = useState({
    vorgangsnummern: [],
    geraete: [],
    kunden: [],
    servicemitarbeiter: [],
    handling: [],
    sourceSystems: [],
    datesDatumUhrzeit: [],
  });

  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('lwDamageLogFilters');
    return saved ? JSON.parse(saved) : {
      vorgangsnummer: null,
      geraet: null,
      kunde: null,
      servicemitarbeiter: null,
      handling: null,
      sourceSystem: null,
      datumUhrzeit: null,  // DD.MM.YYYY
      search: '',
    };
  });

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const limit = 25;

  useEffect(() => {
    localStorage.setItem('lwDamageLogFilters', JSON.stringify(filters));
    setCurrentPage(1);
  }, [filters]);

  const buildQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.append('limit', limit);
    p.append('page', currentPage);
    if (filters.vorgangsnummer)     p.append('vorgangsnummer', filters.vorgangsnummer.value);
    if (filters.geraet)             p.append('geraet', filters.geraet.value);
    if (filters.kunde)              p.append('kunde', filters.kunde.value);
    if (filters.servicemitarbeiter) p.append('servicemitarbeiter', filters.servicemitarbeiter.value);
    if (filters.handling)           p.append('handling', filters.handling.value);
    if (filters.sourceSystem)       p.append('sourceSystem', filters.sourceSystem.value);
    if (filters.datumUhrzeit)       p.append('datumUhrzeit', filters.datumUhrzeit.value);
    if (filters.search)             p.append('search', filters.search);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [filters, currentPage]);


// Öffnet die HTML-Zusammenstellung und springt direkt zum passenden Vorgang
const openDamageHtmlFromRow = (row, includeArchived = false) => {
  if (!row?.itemId) {
    alert('Kein itemId am Datensatz vorhanden – Gerätezusammenstellung kann nicht geöffnet werden.');
    return;
  }

  // 🔑 Token wie bei Items ermitteln
  let token = '';
  try {
    const auth =
      api?.defaults?.headers?.common?.Authorization ||
      api?.defaults?.headers?.Authorization || '';
    if (auth && auth.startsWith('Bearer ')) token = auth.slice(7).trim();
    if (!token) token = localStorage.getItem('token') || '';
    if (!token) token = sessionStorage.getItem('accessToken') || '';
  } catch {}

  if (!token) {
    alert('Kein Token gefunden. Bitte zuerst einloggen.');
    return;
  }

  const base =
    (api?.defaults?.baseURL || '').trim() ||
    (process.env.REACT_APP_BACKEND_URL || '').trim() ||
    window.location.origin;

  const hrefBase = base.replace(/\/+$/, '');
  const usp = new URLSearchParams();
  if (includeArchived) usp.set('includeArchived', '1');
  usp.set('token', token);
  usp.set('_ts', String(Date.now())); // Cache-Busting

  // ⬇️ WICHTIG: direkt zum Eintrag springen (#entry-{LOG_ID})
  const url = `${hrefBase}/lw-damage/by-item/${row.itemId}/html?${usp.toString()}#entry-${row.id}`;
  window.open(url, '_blank', 'noopener');
};


// Ellipsis + Tooltip (zeigt vollen Text im title-Attribut)
const EllipsisCell = ({ text, max = 300 }) => (
  <span
    title={text || ''} // <- zeigt beim Hover den gesamten Text
    style={{
      display: 'inline-block',
      maxWidth: max,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      verticalAlign: 'bottom'
    }}
  >
    {text || ''}
  </span>
);



  // Filter-Optionen laden
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/lw-damage/filter-options');
        setServerFilterOptions(data || {});
      } catch (err) {
        console.error('Filter-Optionen Fehler:', err);
      }
    })();
  }, []);

  // Liste laden
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lw-damage/list${buildQuery}`);
        setRows(data?.data || []);
        setTotalPages(data?.pages || 1);
      } catch (err) {
        console.error('LwDamageLog list Fehler:', err);
      }
    })();
  }, [buildQuery]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };



  const gotoDetail = (id) => navigate(`/lw-damage/${id}`);

    const handleDelete = async (row) => {
    if (!window.confirm(`Eintrag #${row.id} wirklich löschen?`)) return;

    try {
      await api.delete(`/lw-damage/${row.id}`);
      // Lokal aus der Liste entfernen
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (err) {
      console.error('Fehler beim Löschen des Eintrags:', err);
      alert('Löschen fehlgeschlagen. Details in der Konsole.');
    }
  };


  return (
    <div className="lw-damage-log-list">
      <Helmet>
        <title>Liftworker-Schadenmanagement</title>
        <meta name="description" content="Schadenvorgänge – Übersicht" />
      </Helmet>

      <h1>LW-Schadenmanagement</h1>

      {/* Filter */}
      <div style={{ marginBottom: 16 }}>
        <h3></h3>
        <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))', gap: 8 }}>
          <Select
            options={serverFilterOptions.vorgangsnummern?.map(v => ({ value: v, label: v }))}
            placeholder="Vorgangsnummer"
            isClearable
            value={filters.vorgangsnummer}
            onChange={(sel)=>setFilters({ ...filters, vorgangsnummer: sel })}
          />
          <Select
            options={serverFilterOptions.geraete?.map(v => ({ value: v, label: v }))}
            placeholder="Gerät"
            isClearable
            value={filters.geraet}
            onChange={(sel)=>setFilters({ ...filters, geraet: sel })}
          />
          <Select
            options={serverFilterOptions.kunden?.map(v => ({ value: v, label: v }))}
            placeholder="Kunde"
            isClearable
            value={filters.kunde}
            onChange={(sel)=>setFilters({ ...filters, kunde: sel })}
          />
          <Select
            options={serverFilterOptions.servicemitarbeiter?.map(v => ({ value: v, label: v }))}
            placeholder="Servicemitarbeiter"
            isClearable
            value={filters.servicemitarbeiter}
            onChange={(sel)=>setFilters({ ...filters, servicemitarbeiter: sel })}
          />
          <Select
            options={serverFilterOptions.handling?.map(v => ({ value: v, label: v }))}
            placeholder="Handling"
            isClearable
            value={filters.handling}
            onChange={(sel)=>setFilters({ ...filters, handling: sel })}
          />
          <Select
            options={serverFilterOptions.sourceSystems?.map(v => ({ value: v, label: v }))}
            placeholder="Source System"
            isClearable
            value={filters.sourceSystem}
            onChange={(sel)=>setFilters({ ...filters, sourceSystem: sel })}
          />
          <Select
            options={serverFilterOptions.datesDatumUhrzeit?.map(v => ({ value: v, label: v }))}
            placeholder="Meldedatum"
            isClearable
            value={filters.datumUhrzeit}
            onChange={(sel)=>setFilters({ ...filters, datumUhrzeit: sel })}
          />
          <input
            type="text"
            placeholder="Suche (Schaden, Gerät, Vorgang, Kunde …)"
            value={filters.search}
            onChange={(e)=>setFilters({ ...filters, search: e.target.value })}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #ccc' }}
          />
        </div>
      </div>

      {/* Tabelle */}
      {rows?.length ? (
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Vorgangsnummer</th>
              <th>Meldedatum</th>
              <th>Gerät</th>
              <th>LW-ID</th>
              <th>Kunde</th>
              <th>Handling</th>
              <th>Servicemitarbeiter</th>
              <th>Technisch</th>
              <th>Optisch</th>
              <th>Tank / Batterie</th>
              <th>Sauberkeit</th>
              <th>Aktionen</th>
              
            </tr>
          </thead>
          <tbody>
            {[...rows]
              .sort((a,b) => new Date(b.datumUhrzeit || b.createdAt) - new Date(a.datumUhrzeit || a.createdAt))
              .map(r => {
                
                return (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.vorgangsnummer || '-'}</td>
                    <td>{fmtDate(r.datumUhrzeit)}</td>
                     <td
   onClick={() => r.itemId && openDamageHtmlFromRow(r)}
   onKeyDown={(e) => {
     if ((e.key === 'Enter' || e.key === ' ') && r.itemId) openDamageHtmlFromRow(r);
   }}
   role="button"
   tabIndex={0}
   title={r.itemId ? 'Schadenn anzeigen' : 'Keine itemId vorhanden'}
   style={{
     color: r.itemId ? '#007bff' : '#666',
     textDecoration: r.itemId ? 'underline' : 'none',
     cursor: r.itemId ? 'pointer' : 'not-allowed'
   }}
 >
   {r.geraet || '-'}
 </td>
                    <td>{r.lwId || '-'}</td>
                    <td>{r.kunde || '-'}</td>
                    <td>{r.handling || '-'}</td>
                    <td>{r.servicemitarbeiter || '-'}</td>
                    <td>
  <EllipsisCell text={r.technischerSchaden} max={300} />
</td>
<td>
  <EllipsisCell text={r.optischerSchaden} max={300} />
</td>
<td>
  <EllipsisCell text={r.tankBatterieStatus} max={260} />
</td>
<td>
  <EllipsisCell text={r.geraetSauberkeit} max={260} />
</td>
 {/* 👇 NEUE Spalte: Aktionen */}
          <td>
            <button
              type="button"
              onClick={() => handleDelete(r)}
              style={{
                padding: '4px 8px',
                borderRadius: 4,
                border: '1px solid #cc0000',
                background: '#ff4d4d',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              X
            </button>
          </td>
                  
                  </tr>
                );
              })}
          </tbody>
        </table>
      ) : (
        <p>Keine Einträge vorhanden.</p>
      )}

      {/* Pagination */}
      <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
        <button
          onClick={() => setCurrentPage(p => (p > 1 ? p - 1 : 1))}
          disabled={currentPage === 1}
          style={{ padding:'8px 12px', background:'#9b9b9b', color:'#fff', border:'none', borderRadius:6 }}
        >
          Vorherige
        </button>
        <span>Seite {currentPage} von {totalPages}</span>
        <button
          onClick={() => setCurrentPage(p => (p < totalPages ? p + 1 : p))}
          disabled={currentPage === totalPages}
          style={{ padding:'8px 12px', background:'#9b9b9b', color:'#fff', border:'none', borderRadius:6 }}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

export default LwDamageLogList;
